import { useEffect, useState } from 'react';
import { Lock, LogOut, Table2, FileText, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const LANGS = ['pt-br', 'es', 'en'];

function deepGet(obj, path) {
  return path.split('.').reduce((n, k) => (n && typeof n === 'object' ? n[k] : undefined), obj);
}

export default function Admin() {
  const [user, setUser] = useState('checking');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [tab, setTab] = useState('leads');
  const [leads, setLeads] = useState([]);
  const [lang, setLang] = useState('pt-br');
  const [editables, setEditables] = useState([]);
  const [defaults, setDefaults] = useState({});
  const [overrides, setOverrides] = useState({});
  const [savedMsg, setSavedMsg] = useState('');

  const check = async () => {
    try {
      const r = await fetch(`${API}/api/auth/me`, { credentials: 'include' });
      setUser(r.ok ? await r.json() : null);
    } catch {
      setUser(null);
    }
  };
  useEffect(() => { check(); }, []);

  const login = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const r = await fetch(`${API}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => null);
        setError(typeof d?.detail === 'string' ? d.detail : 'Falha no login');
        return;
      }
      setUser(await r.json());
    } catch {
      setError('Falha no login');
    }
  };

  const logout = async () => {
    await fetch(`${API}/api/auth/logout`, { method: 'POST', credentials: 'include' });
    setUser(null);
  };

  const loadLeads = async () => {
    const r = await fetch(`${API}/api/admin/leads`, { credentials: 'include' });
    if (r.ok) setLeads(await r.json());
  };

  const loadContent = async (l) => {
    const content = (await import(`@/content/${l}.js`)).default;
    setEditables(content._editable || []);
    setDefaults(content);
    const r = await fetch(`${API}/api/content/${l}`);
    const d = r.ok ? await r.json() : { overrides: {} };
    setOverrides(d.overrides || {});
  };

  useEffect(() => {
    if (user && tab === 'leads') loadLeads();
    if (user && tab === 'content') loadContent(lang);
  }, [user, tab, lang]);

  const saveContent = async () => {
    setSavedMsg('');
    const r = await fetch(`${API}/api/admin/content`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ lang, overrides }),
    });
    setSavedMsg(r.ok ? 'Alterações salvas ✓' : 'Erro ao salvar');
    setTimeout(() => setSavedMsg(''), 3000);
  };

  const inputCls = 'w-full rounded-xl border border-brand-line bg-white px-4 py-3 text-[14px] focus:border-brand-blue focus:outline-none';

  if (user === 'checking') {
    return <div className="flex min-h-screen items-center justify-center text-brand-muted">...</div>;
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-brand-mist px-6" data-testid="admin-login">
        <form onSubmit={login} className="card-soft w-full max-w-sm p-8">
          <p className="font-display text-xl font-bold text-brand-navy">Painel Rodotrack</p>
          <p className="mt-1 text-[13px] text-brand-muted">Acesso restrito — leads e conteúdos do site.</p>
          <label className="mt-6 block">
            <span className="mb-1.5 block text-[13px] font-semibold">E-mail</span>
            <input data-testid="admin-input-email" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} type="email" required />
          </label>
          <label className="mt-4 block">
            <span className="mb-1.5 block text-[13px] font-semibold">Senha</span>
            <input data-testid="admin-input-password" className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} type="password" required />
          </label>
          {error && <p data-testid="admin-error-msg" className="mt-3 text-[13px] font-medium text-red-600">{error}</p>}
          <button data-testid="admin-login-btn" className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-navy px-6 py-3 text-sm font-semibold text-white transition hover:bg-brand-blue">
            <Lock className="h-4 w-4" /> Entrar
          </button>
          <a href="/pt-br" className="mt-4 block text-center text-[12px] text-brand-muted hover:text-brand-blue">← Voltar ao site</a>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-mist pt-10" data-testid="admin-panel">
      <div className="container-x max-w-5xl">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-display text-2xl font-bold text-brand-navy">Painel Rodotrack</p>
            <p className="text-[13px] text-brand-muted">{user.email}</p>
          </div>
          <button data-testid="admin-logout-btn" onClick={logout} className="inline-flex items-center gap-2 rounded-full border border-brand-line bg-white px-4 py-2 text-[13px] font-semibold text-brand-navy hover:border-brand-blue">
            <LogOut className="h-4 w-4" /> Sair
          </button>
        </div>

        <div className="mt-6 flex gap-2">
          <button data-testid="admin-tab-leads" onClick={() => setTab('leads')} className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-semibold transition ${tab === 'leads' ? 'bg-brand-blue text-white' : 'border border-brand-line bg-white text-brand-navy'}`}>
            <Table2 className="h-4 w-4" /> Leads ({leads.length})
          </button>
          <button data-testid="admin-tab-content" onClick={() => setTab('content')} className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-semibold transition ${tab === 'content' ? 'bg-brand-blue text-white' : 'border border-brand-line bg-white text-brand-navy'}`}>
            <FileText className="h-4 w-4" /> Conteúdos
          </button>
        </div>

        {tab === 'leads' && (
          <div className="mt-6 space-y-3 pb-16" data-testid="admin-leads-list">
            {leads.length === 0 && <p className="rounded-2xl border border-brand-line bg-white p-8 text-center text-[14px] text-brand-muted">Nenhuma solicitação recebida ainda.</p>}
            {leads.map((l) => (
              <div key={l.id} data-testid={`admin-lead-${l.id}`} className="rounded-2xl border border-brand-line bg-white p-5">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                  <p className="font-semibold text-brand-navy">{l.name}</p>
                  <span className="rounded-full bg-brand-ice px-2.5 py-0.5 font-mono text-[11px] font-bold text-brand-blue">{l.country}</span>
                  <span className="text-[12px] text-brand-muted">{l.vehicle}</span>
                  <span className="ml-auto font-mono text-[11px] text-brand-muted">{new Date(l.created_at).toLocaleString('pt-BR')}</span>
                </div>
                <p className="mt-2 text-[13px] text-brand-muted">{l.email} · {l.phone}</p>
                {l.message && <p className="mt-2 rounded-xl bg-brand-mist p-3 text-[13.5px] text-brand-navy/85">{l.message}</p>}
              </div>
            ))}
          </div>
        )}

        {tab === 'content' && (
          <div className="mt-6 pb-16" data-testid="admin-content-editor">
            <div className="flex items-center gap-3">
              <select data-testid="admin-content-lang" className={`${inputCls} max-w-[200px]`} value={lang} onChange={(e) => setLang(e.target.value)}>
                {LANGS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
              <button data-testid="admin-content-save" onClick={saveContent} className="inline-flex items-center gap-2 rounded-full bg-brand-blue px-6 py-3 text-[13px] font-semibold text-white transition hover:bg-brand-bluedark">
                <RefreshCw className="h-4 w-4" /> Salvar
              </button>
              {savedMsg && <span data-testid="admin-content-saved" className="text-[13px] font-semibold text-brand-green">{savedMsg}</span>}
            </div>
            <div className="mt-5 grid gap-4 md:grid-cols-2">
              {editables.map((f) => (
                <label key={f.path} className="block rounded-2xl border border-brand-line bg-white p-4">
                  <span className="mb-1.5 flex items-center justify-between text-[12px] font-semibold text-brand-navy">
                    {f.label}
                    <code className="font-mono text-[10px] text-brand-muted">{f.path}</code>
                  </span>
                  <textarea
                    data-testid={`admin-field-${f.path.replace(/\./g, '-')}`}
                    rows={2}
                    className="w-full rounded-lg border border-brand-line bg-brand-mist px-3 py-2 text-[13px] focus:border-brand-blue focus:outline-none"
                    value={overrides[f.path] !== undefined ? overrides[f.path] : deepGet(defaults, f.path) ?? ''}
                    onChange={(e) => setOverrides({ ...overrides, [f.path]: e.target.value })}
                  />
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}